import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../api/api';
import { useAuth } from '../context/AuthContext';

const GameDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [game, setGame] = useState(null);
  const [homeStats, setHomeStats] = useState([]);
  const [awayStats, setAwayStats] = useState([]);
  const [matchReport, setMatchReport] = useState(null);
  const [homeSortConfig, setHomeSortConfig] = useState({ key: 'points', direction: 'desc' });
  const [awaySortConfig, setAwaySortConfig] = useState({ key: 'points', direction: 'desc' });
  const [reportLoading, setReportLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchGameDetails();
  }, [id]);

  const fetchGameDetails = async () => {
    try {
      setLoading(true);
      const [gameRes, statsRes] = await Promise.all([
        api.get(`/games/${id}`),
        api.get(`/stats/game/${id}`)
      ]);

      setGame(gameRes.data);
      
      // Separate stats by team
      const home = statsRes.data.filter(s => s.teamId === gameRes.data.homeTeamId);
      const away = statsRes.data.filter(s => s.teamId === gameRes.data.awayTeamId);
      
      setHomeStats(home);
      setAwayStats(away);

      // Fetch match report (ignore 404)
      try {
        const reportRes = await api.get(`/match-reports/game/${id}`);
        setMatchReport(reportRes.data);
      } catch (e) {
        // No report available — that's fine
        setMatchReport(null);
      }
    } catch (err) {
      setError('Failed to load game details');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setReportLoading(true);
    try {
      await api.post(`/match-reports/generate/${id}`);
      try {
        const reportRes = await api.get(`/match-reports/game/${id}`);
        setMatchReport(reportRes.data);
      } catch (e) {
        setMatchReport(null);
      }
    } catch (err) {
      console.error('Report generation failed:', err);
      alert(err.response?.data?.reason || 'Bericht konnte nicht generiert werden');
    } finally {
      setReportLoading(false);
    }
  };

  const handleToggleVisibility = async () => {
    if (!matchReport) return;
    try {
      await api.put(`/match-reports/${matchReport.id}/visibility`, {
        visible: !matchReport.visible
      });
      setMatchReport({ ...matchReport, visible: !matchReport.visible });
    } catch (err) {
      console.error('Toggle visibility failed:', err);
    }
  };

  const handleDeleteReport = async () => {
    if (!matchReport || !window.confirm('Spielbericht wirklich löschen?')) return;
    try {
      await api.delete(`/match-reports/${matchReport.id}`);
      setMatchReport(null);
    } catch (err) {
      console.error('Delete report failed:', err);
    }
  };

  const calculateTeamTotals = (stats) => {
    return stats.reduce((totals, stat) => ({
      minutes: totals.minutes + stat.minutes,
      points: totals.points + stat.points,
      rebounds: totals.rebounds + stat.rebounds,
      assists: totals.assists + stat.assists,
      steals: totals.steals + stat.steals,
      blocks: totals.blocks + stat.blocks,
      turnovers: totals.turnovers + stat.turnovers,
      fieldGoalsMade: totals.fieldGoalsMade + stat.fieldGoalsMade,
      fieldGoalsAttempted: totals.fieldGoalsAttempted + stat.fieldGoalsAttempted,
      threePointersMade: totals.threePointersMade + stat.threePointersMade,
      threePointersAttempted: totals.threePointersAttempted + stat.threePointersAttempted,
      freeThrowsMade: totals.freeThrowsMade + stat.freeThrowsMade,
      freeThrowsAttempted: totals.freeThrowsAttempted + stat.freeThrowsAttempted
    }), {
      minutes: 0,
      points: 0,
      rebounds: 0,
      assists: 0,
      steals: 0,
      blocks: 0,
      turnovers: 0,
      fieldGoalsMade: 0,
      fieldGoalsAttempted: 0,
      threePointersMade: 0,
      threePointersAttempted: 0,
      freeThrowsMade: 0,
      freeThrowsAttempted: 0
    });
  };

  const renderStatsTable = (stats, teamName, sortConfig, setSortConfig) => {
    if (stats.length === 0) {
      return <p className="text-gray-500 dark:text-gray-400 italic">No stats available</p>;
    }

    const handleSort = (key) => {
      setSortConfig(prev =>
        prev.key === key
          ? { key, direction: prev.direction === 'desc' ? 'asc' : 'desc' }
          : { key, direction: 'desc' }
      );
    };

    const sortIndicator = (key) => {
      if (sortConfig.key !== key) return <span className="ml-1 text-gray-300">↕</span>;
      return sortConfig.direction === 'desc'
        ? <span className="ml-1">↓</span>
        : <span className="ml-1">↑</span>;
    };

    const getSortValue = (stat, key) => {
      if (key === 'fieldGoalsMade') return stat.fieldGoalsMade;
      if (key === 'threePointersMade') return stat.threePointersMade;
      if (key === 'freeThrowsMade') return stat.freeThrowsMade;
      if (key === 'name') {
        return `${stat.player?.lastName ?? ''} ${stat.player?.firstName ?? ''}`;
      }
      return stat[key] ?? 0;
    };

    const sortedStats = [...stats].sort((a, b) => {
      const aVal = getSortValue(a, sortConfig.key);
      const bVal = getSortValue(b, sortConfig.key);
      if (typeof aVal === 'string') {
        return sortConfig.direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
    });

    const thClass = "px-1 md:px-2 py-2 text-center border cursor-pointer select-none hover:bg-gray-200 dark:bg-gray-600 whitespace-nowrap";

    const totals = calculateTeamTotals(stats);
    const fgPct = totals.fieldGoalsAttempted > 0 
      ? ((totals.fieldGoalsMade / totals.fieldGoalsAttempted) * 100).toFixed(1) 
      : '0.0';
    const threePct = totals.threePointersAttempted > 0 
      ? ((totals.threePointersMade / totals.threePointersAttempted) * 100).toFixed(1) 
      : '0.0';
    const ftPct = totals.freeThrowsAttempted > 0 
      ? ((totals.freeThrowsMade / totals.freeThrowsAttempted) * 100).toFixed(1) 
      : '0.0';

    return (
      <div className="overflow-x-auto mobile-scroll">
        <table className="min-w-full bg-white dark:bg-gray-800 border text-xs md:text-sm">
          <thead className="bg-gray-100 dark:bg-gray-700">
            <tr>
              <th
                className="px-2 md:px-4 py-2 text-left border sticky left-0 bg-gray-100 dark:bg-gray-700 z-10 cursor-pointer select-none hover:bg-gray-200 dark:bg-gray-600"
                onClick={() => handleSort('name')}
              >Player{sortIndicator('name')}</th>
              <th className={thClass} onClick={() => handleSort('minutes')}>MIN{sortIndicator('minutes')}</th>
              <th className={thClass} onClick={() => handleSort('points')}>PTS{sortIndicator('points')}</th>
              <th className={thClass} onClick={() => handleSort('rebounds')}>REB{sortIndicator('rebounds')}</th>
              <th className={thClass} onClick={() => handleSort('assists')}>AST{sortIndicator('assists')}</th>
              <th className={thClass} onClick={() => handleSort('steals')}>STL{sortIndicator('steals')}</th>
              <th className={thClass} onClick={() => handleSort('blocks')}>BLK{sortIndicator('blocks')}</th>
              <th className={thClass} onClick={() => handleSort('turnovers')}>TO{sortIndicator('turnovers')}</th>
              <th className={thClass} onClick={() => handleSort('fieldGoalsMade')}>FG{sortIndicator('fieldGoalsMade')}</th>
              <th className={thClass} onClick={() => handleSort('threePointersMade')}>3PT{sortIndicator('threePointersMade')}</th>
              <th className={thClass} onClick={() => handleSort('freeThrowsMade')}>FT{sortIndicator('freeThrowsMade')}</th>
            </tr>
          </thead>
          <tbody>
            {sortedStats.map((stat) => (
              <tr key={stat.id} className="hover:bg-gray-50 dark:bg-gray-800">
                <td className="px-4 py-2 border font-medium">
                  <Link 
                    to={`/player/${stat.playerId}`}
                    className="text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    {stat.player?.firstName} {stat.player?.lastName}
                  </Link>
                </td>
                <td className="px-2 py-2 text-center border">{stat.minutes}</td>
                <td className="px-2 py-2 text-center border font-semibold">{stat.points}</td>
                <td className="px-2 py-2 text-center border">{stat.rebounds}</td>
                <td className="px-2 py-2 text-center border">{stat.assists}</td>
                <td className="px-2 py-2 text-center border">{stat.steals}</td>
                <td className="px-2 py-2 text-center border">{stat.blocks}</td>
                <td className="px-2 py-2 text-center border">{stat.turnovers}</td>
                <td className="px-2 py-2 text-center border">
                  {stat.fieldGoalsMade}-{stat.fieldGoalsAttempted}
                </td>
                <td className="px-2 py-2 text-center border">
                  {stat.threePointersMade}-{stat.threePointersAttempted}
                </td>
                <td className="px-2 py-2 text-center border">
                  {stat.freeThrowsMade}-{stat.freeThrowsAttempted}
                </td>
              </tr>
            ))}
            {/* Team Totals Row */}
            <tr className="bg-gray-200 dark:bg-gray-600 font-bold">
              <td className="px-4 py-2 border">Team Totals</td>
              <td className="px-2 py-2 text-center border">-</td>
              <td className="px-2 py-2 text-center border">{totals.points}</td>
              <td className="px-2 py-2 text-center border">{totals.rebounds}</td>
              <td className="px-2 py-2 text-center border">{totals.assists}</td>
              <td className="px-2 py-2 text-center border">{totals.steals}</td>
              <td className="px-2 py-2 text-center border">{totals.blocks}</td>
              <td className="px-2 py-2 text-center border">{totals.turnovers}</td>
              <td className="px-2 py-2 text-center border">
                {totals.fieldGoalsMade}-{totals.fieldGoalsAttempted}
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">({fgPct}%)</span>
              </td>
              <td className="px-2 py-2 text-center border">
                {totals.threePointersMade}-{totals.threePointersAttempted}
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">({threePct}%)</span>
              </td>
              <td className="px-2 py-2 text-center border">
                {totals.freeThrowsMade}-{totals.freeThrowsAttempted}
                <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">({ftPct}%)</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-xl">Loading game details...</div>
      </div>
    );
  }

  if (error || !game) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="bg-red-100 dark:bg-red-900/30 border border-red-400 dark:border-red-700 text-red-700 dark:text-red-400 px-4 py-3 rounded">
          {error || 'Game not found'}
        </div>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 text-blue-600 dark:text-blue-400 hover:underline"
        >
          ← Go Back
        </button>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-4 md:py-8">
      {/* Header */}
      <button
        onClick={() => navigate(-1)}
        className="mb-3 md:mb-4 text-blue-600 dark:text-blue-400 hover:underline flex items-center text-sm md:text-base"
      >
        ← Back
      </button>

      {/* Game Score Header */}
      <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-4 md:p-6 mb-6 md:mb-8">
        <div className="text-center mb-4">
          <p className="text-sm md:text-base text-gray-600 dark:text-gray-400">
            {new Date(game.gameDate).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
          <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1">
            {game.Season?.year} Season
            {game.isPlayoffGame && ' • Playoff Game'}
          </p>
        </div>

        <div className="flex flex-col md:flex-row justify-center items-center gap-4 md:gap-8">
          {/* Away Team */}
          <div className="text-center flex flex-col items-center">
            {game.awayTeam?.logoUrl && (
              <img 
                src={game.awayTeam.logoUrl} 
                alt={`${game.awayTeam.name} logo`}
                className="w-16 h-16 md:w-20 md:h-20 object-contain mb-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <h2 className="text-lg md:text-2xl font-bold mb-2">
              {game.awayTeam?.city} {game.awayTeam?.name}
            </h2>
            <div className={`text-4xl md:text-5xl font-bold ${
              game.awayScore > game.homeScore ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'
            }`}>
              {game.awayScore || 0}
            </div>
          </div>

          {/* VS */}
          <div className="text-2xl md:text-3xl font-bold text-gray-400">@</div>

          {/* Home Team */}
          <div className="text-center flex flex-col items-center">
            {game.homeTeam?.logoUrl && (
              <img 
                src={game.homeTeam.logoUrl} 
                alt={`${game.homeTeam.name} logo`}
                className="w-16 h-16 md:w-20 md:h-20 object-contain mb-2"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            <h2 className="text-lg md:text-2xl font-bold mb-2">
              {game.homeTeam?.city} {game.homeTeam?.name}
            </h2>
            <div className={`text-4xl md:text-5xl font-bold ${
              game.homeScore > game.awayScore ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'
            }`}>
              {game.homeScore || 0}
            </div>
          </div>
        </div>

        {game.notes && (
          <div className="mt-4 text-center text-sm md:text-base text-gray-600 dark:text-gray-400 italic">
            {game.notes}
          </div>
        )}

        {/* Link to match report if available */}
        {matchReport && (
          <div className="mt-4 text-center">
            <a href="#match-report" className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 hover:text-blue-800 font-medium text-sm">
              📝 Spielbericht lesen ↓
            </a>
          </div>
        )}
      </div>

      {/* Away Team Stats */}
      <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-4 md:p-6 mb-4 md:mb-6">
        <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-gray-800 dark:text-gray-200">
          {game.awayTeam?.city} {game.awayTeam?.name} Box Score
        </h3>
        {renderStatsTable(awayStats, `${game.awayTeam?.city} ${game.awayTeam?.name}`, awaySortConfig, setAwaySortConfig)}
      </div>

      {/* Home Team Stats */}
      <div className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-4 md:p-6">
        <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4 text-gray-800 dark:text-gray-200">
          {game.homeTeam?.city} {game.homeTeam?.name} Box Score
        </h3>
        {renderStatsTable(homeStats, `${game.homeTeam?.city} ${game.homeTeam?.name}`, homeSortConfig, setHomeSortConfig)}
      </div>

      {/* Match Report */}
      {matchReport && (
        <div id="match-report" className="bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 rounded-lg p-4 md:p-6 mt-6">
          {/* Headline */}
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 mb-3 border-b-2 border-red-500 pb-2">
            📝 {matchReport.headline}
          </h2>

          {/* Recap */}
          <p className="text-gray-700 dark:text-gray-300 text-sm md:text-base leading-relaxed mb-6">
            {matchReport.recap}
          </p>

          {/* Top Performers */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Away Top Performers */}
            <div>
              <h4 className="text-base md:text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                🏆 Top Performer – {game.awayTeam?.city} {game.awayTeam?.name}
              </h4>
              <div className="space-y-3">
                {(matchReport.topPerformersAway || []).map((p, i) => (
                  <div key={i} className={`rounded-lg p-3 border ${i === 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-gray-900 dark:text-gray-100 text-sm md:text-base">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {p.playerName}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{p.minutes} MIN</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs md:text-sm">
                      <span className="font-semibold text-blue-700 dark:text-blue-400">{p.points} PTS</span>
                      <span className="text-gray-600 dark:text-gray-400">{p.rebounds} REB</span>
                      <span className="text-gray-600 dark:text-gray-400">{p.assists} AST</span>
                      <span className="text-gray-600 dark:text-gray-400">{p.steals} STL</span>
                      <span className="text-gray-600 dark:text-gray-400">{p.blocks} BLK</span>
                      <span className="text-red-500">{p.turnovers} TO</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Home Top Performers */}
            <div>
              <h4 className="text-base md:text-lg font-bold text-gray-800 dark:text-gray-200 mb-3 flex items-center gap-2">
                🏆 Top Performer – {game.homeTeam?.city} {game.homeTeam?.name}
              </h4>
              <div className="space-y-3">
                {(matchReport.topPerformersHome || []).map((p, i) => (
                  <div key={i} className={`rounded-lg p-3 border ${i === 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'}`}>
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-gray-900 dark:text-gray-100 text-sm md:text-base">
                        {i === 0 ? '🥇' : i === 1 ? '🥈' : '🥉'} {p.playerName}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">{p.minutes} MIN</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-xs md:text-sm">
                      <span className="font-semibold text-blue-700 dark:text-blue-400">{p.points} PTS</span>
                      <span className="text-gray-600 dark:text-gray-400">{p.rebounds} REB</span>
                      <span className="text-gray-600 dark:text-gray-400">{p.assists} AST</span>
                      <span className="text-gray-600 dark:text-gray-400">{p.steals} STL</span>
                      <span className="text-gray-600 dark:text-gray-400">{p.blocks} BLK</span>
                      <span className="text-red-500">{p.turnovers} TO</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Report meta */}
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <span className="text-xs text-gray-400">
              Automatisch generiert am {new Date(matchReport.generatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
            </span>
            
            {/* Admin Controls */}
            {isAdmin && (
              <div className="flex gap-2">
                <button
                  onClick={handleGenerateReport}
                  disabled={reportLoading}
                  className="text-xs bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 text-blue-700 dark:text-blue-400 px-3 py-1 rounded"
                >
                  {reportLoading ? '...' : '🔄 Neu generieren'}
                </button>
                <button
                  onClick={handleToggleVisibility}
                  className="text-xs bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 px-3 py-1 rounded"
                >
                  {matchReport.visible ? '👁️ Ausblenden' : '👁️ Einblenden'}
                </button>
                <button
                  onClick={handleDeleteReport}
                  className="text-xs bg-red-100 dark:bg-red-900/30 hover:bg-red-200 text-red-700 dark:text-red-400 px-3 py-1 rounded"
                >
                  🗑️ Löschen
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Admin: Generate report button when no report exists */}
      {!matchReport && isAdmin && game.played && (
        <div className="mt-6 text-center">
          <button
            onClick={handleGenerateReport}
            disabled={reportLoading}
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white px-6 py-2 rounded-lg font-medium text-sm"
          >
            {reportLoading ? 'Wird generiert...' : '📝 Spielbericht generieren'}
          </button>
        </div>
      )}
    </div>
  );
};

export default GameDetail;
